import { rootApi } from '../../services/rootApi';
import { quizApi, sectionApi, pageApi, courseApi } from '../../utils/urls';

/**
 * RTK Query API slice for quizes
 */
export const quizesApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchQuizes: builder.query({
      query: ({ courseId, userId }) => ({
        url: quizApi(),
        method: 'POST',
        body: {
          data: { courseId },
          method: 'read',
        },
      }),
      transformResponse: (response) => {
        const quizes = response.data;
        const quizData = [];

        // Process each quiz
        for (const [id, quiz] of Object.entries(quizes)) {
          const tasks = quiz.tasks;
          const tasksInfo = {};
          let usersResults = {};
          let maxPoints = 0;
          let userPoints = {};

          // Process each task
          for (const [taskId, task] of Object.entries(tasks)) {
            maxPoints += task.maxPoints;

            // Check for user results
            const results = task.taskResults;
            results.forEach((result) => {
              const user = result.user;
              const quizByUser = !!quiz.userdata && !!quiz.userdata[user.id] && quiz.userdata[user.id]?.pages && quiz.userdata[user.id]?.pages[id];
              const lastStatistics = quizByUser && quizByUser[quizByUser?.length - 1];
              userPoints[user.id] = userPoints[user.id] ? result.points + userPoints[user.id] : result.points;

              usersResults = {
                ...usersResults,
                [user.id]: {
                  id: user.id,
                  name: user.name,
                  totalPoints: userPoints[user.id],
                  isComplete: !!quiz.userStats && quiz.userStats[user.id]?.isCompleted,
                  isLocked: !!quiz.userdata && quiz.userdata[user.id]?.locked,
                  statistics: !!quiz.userdata && !!quiz.userdata[user.id]?.pages && quiz.userdata[user.id]?.pages[id],
                  status: lastStatistics?.status,
                  score: lastStatistics?.score,
                  endTime: lastStatistics?.endTime,
                  startTime: lastStatistics?.startTime,
                  duration: lastStatistics?.duration,
                  total: lastStatistics?.total,
                  questions: {
                    ...usersResults[user.id]?.questions,
                    [taskId]: {
                      questionId: taskId,
                      value: result.body.value,
                      files: result.files,
                      created: result.created,
                      grade: result.body.value.toString() === task.correctResponse.value.toString() ? 1 : 0,
                      points: result.points,
                    },
                  },
                },
              };
            });

            tasksInfo[taskId] = {
              id: taskId,
              name: task.caption.main,
              options: { ...task.caption.items },
              correctOptions: task.correctResponse.value,
              maxPoints: task.maxPoints,
              type: task.type,
            };
          }

          let totalPassed = 0;
          for (const [, user] of Object.entries(usersResults)) {
            totalPassed += user.status === 'QUIZ_SUCCESS' ? 1 : 0;
          }

          const users = Object.values(usersResults);
          quizData.push({
            id: +id,
            name: quiz.page?.name,
            maxPoints,
            totalPassed,
            usersCount: users.length,
            users,
            questions: Object.values(tasksInfo),
          });
        }

        return quizData;
      },
      providesTags: (result, error, { courseId }) => [{ type: 'Quizes', id: courseId }, ...(result?.map((quiz) => ({ type: 'Quiz', id: quiz.id })) || [])],
    }),

    resetUserQuiz: builder.mutation({
      query: ({ quiz, userId }) => ({
        url: quizApi(),
        method: 'POST',
        body: {
          data: { userId, pageId: quiz.id },
          method: 'reset',
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Quizes', id: courseId }],
    }),

    createQuiz: builder.mutation({
      async queryFn(quizData, { dispatch, getState }, extraOptions, baseQuery) {
        // Step 1: CREATE QUIZ SECTION AT COURSE (SECTION/CREATE)
        const sectionRes = await baseQuery({
          url: sectionApi(),
          method: 'POST',
          body: {
            data: {
              courseId: quizData.courseId,
              name: 'Quiz',
              type: 'quiz',
            },
            method: 'create',
          },
        });

        if (sectionRes.error) return { error: sectionRes.error };
        const sectionId = sectionRes.data.data.id;

        // Step 2: CREATE PAGE INSIDE QUIZ SECTION (PAGE/CREATE)
        const pageRes = await baseQuery({
          url: pageApi(),
          method: 'POST',
          body: {
            data: {
              sectionId,
              name: quizData.name,
              type: 'quiz',
            },
            method: 'create',
          },
        });

        if (pageRes.error) return { error: pageRes.error };
        const pageId = pageRes.data.data.id;

        // Step 3: ENABLE QUIZ FOR COURSE / UPDATE QUIZ PAGE (COURSE/UPDATE)
        const { descriptionText, failText, successText, welcomeText, repeatable, threshold, hideAnswers, quizPages, settings } = quizData;
        const courseRes = await baseQuery({
          url: courseApi(),
          method: 'POST',
          body: {
            data: {
              id: quizData.courseId,
              settings: {
                ...settings,
                quiz: {
                  ...settings.quiz,
                  enabled: true,
                  pages: {
                    ...quizPages,
                    [pageId]: {
                      descriptionText,
                      failText,
                      successText,
                      welcomeText,
                      repeatable,
                      threshold,
                      hideAnswers,
                    },
                  },
                },
              },
            },
            method: 'update',
          },
        });

        if (courseRes.error) return { error: courseRes.error };

        // Return both the updated course and the created pageId for consumers that need it
        return { data: { course: courseRes.data.data, pageId } };
      },
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'Quizes', id: courseId },
        { type: 'Course', id: courseId },
      ],
    }),

    completeQuiz: builder.mutation({
      query: ({ pageId }) => ({
        url: quizApi(),
        method: 'POST',
        body: {
          data: { pageId },
          method: 'complete',
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { pageId }) => [{ type: 'Quiz', id: pageId }],
    }),

    deleteQuizByPage: builder.mutation({
      query: ({ quizId, courseId }) => ({
        url: quizApi(),
        method: 'POST',
        body: {
          data: {
            pageId: quizId,
            courseId,
          },
          method: 'deleteQuizByPage',
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Quizes', id: courseId }],
    }),
  }),
});

export const { useFetchQuizesQuery, useResetUserQuizMutation, useCreateQuizMutation, useCompleteQuizMutation, useDeleteQuizByPageMutation } = quizesApi;
